import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { PAYOUT_FALLBACK } from '../legal/payoutFallback';
import { formatLegalDate, resolveLegalPolicy } from '../legal/resolveLegalPolicy';
import PageSeo from '../components/seo/PageSeo';

const PoliticaRepassesVideomaker = () => {
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(PAYOUT_FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const { data } = await api.get('/lgpd/payout-policy');
        setPolicy(resolveLegalPolicy(data, PAYOUT_FALLBACK, '1.1'));
      } catch (error) {
        console.error('Erro ao carregar política de repasses:', error);
        setPolicy(PAYOUT_FALLBACK);
      } finally {
        setLoading(false);
      }
    };

    fetchPolicy();
  }, []);

  const renderWithBold = (text) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
  };

  const formatContent = (content) => {
    if (typeof content !== 'string') return null;
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('# ')) {
          return (
            <h1 key={index} className="text-3xl font-bold mb-4 mt-8 text-gray-900 dark:text-white">
              {renderWithBold(line.substring(2))}
            </h1>
          );
        }
        if (line.startsWith('## ')) {
          return (
            <h2 key={index} className="text-2xl font-bold mb-3 mt-6 text-gray-900 dark:text-white">
              {renderWithBold(line.substring(3))}
            </h2>
          );
        }
        if (line.startsWith('### ')) {
          return (
            <h3 key={index} className="text-xl font-bold mb-2 mt-4 text-gray-900 dark:text-white">
              {renderWithBold(line.substring(4))}
            </h3>
          );
        }
        if (line.startsWith('- ')) {
          return (
            <li key={index} className="mb-2 ml-4 text-gray-700 dark:text-gray-300">
              {renderWithBold(line.substring(2))}
            </li>
          );
        }
        if (line.trim() === '') {
          return <br key={index} />;
        }
        return (
          <p key={index} className="mb-4 text-gray-700 dark:text-gray-300">
            {renderWithBold(line)}
          </p>
        );
      });
  };

  return (
    <div className="min-h-full">
      <PageSeo
        title="Política de Repasses para Videomakers | Ufokko"
        description="Como funcionam os repasses via Stripe para videomakers na Ufokko após aprovação da entrega pelo cliente."
        path="/politica-repasses-videomaker"
      />
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-12"
        >
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">
                Carregando política de repasses...
              </p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Política de Repasses ao Videomaker
                </h1>
                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <span>Versão {policy.version}</span>
                  <span>•</span>
                  <span>Última atualização: {formatLegalDate(policy.last_updated)}</span>
                </div>
              </div>

              <div className="prose prose-lg dark:prose-invert max-w-none">
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {formatContent(policy.content)}
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-gradient-to-r from-[#0E76FF] to-[#2563EB] text-white rounded-full font-semibold hover:shadow-lg transition-shadow"
                >
                  Voltar para a página inicial
                </button>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default PoliticaRepassesVideomaker;
